import { CargoCoolingRange, CargoType, CargoTypeSub, ServiceType } from '@prisma/client';

import { AirCanada } from '../../../api/src/lib/integrations/ac/typings';
import { Emirates } from '../../../api/src/lib/integrations/ek/typings';
import { IATA } from '../../../api/src/lib/integrations/iata';
import { Qatar } from '../../../api/src/lib/integrations/qr/typings';
import { Turkish } from '../../../api/src/lib/integrations/tk/typings';

// Commodity catalog (canonical tree + per-airline catalogs). See plans/2026-10-commodity-catalog/plan.md.
export * from './commodities/canonical';
export * from './commodities/commodity-node';
export * from './commodities/provider-commodity-catalogs';
export * from './commodities/resolve-provider-commodity';
export * from './commodities/types';

export type AirlineProductToPeli<T> = {
  name: string;
  code: T;
  shcs?: string[];
  cargoType: CargoType;
  cargoTypeSub?: CargoTypeSub | null;
  serviceType: ServiceType;

  visibleTo?: {
    cargoTypes: (CargoType | null | undefined)[];
    cargoTypeSubs?: (CargoTypeSub | null | undefined)[];
    serviceTypes?: ServiceType[];
    cargoCoolingRanges?: CargoCoolingRange[];
    unNumbers?: string[];
  };
};

export type AirlineProductToPeliMap<T extends string> = Record<T, AirlineProductToPeli<T>>;

export const EK_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Emirates.ProductCode> = {
  [Emirates.ProductCode.GCR]: {
    name: 'Emirates AirFreight',
    code: Emirates.ProductCode.GCR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.AXA]: {
    name: 'Emirates AirFreight Priority',
    code: Emirates.ProductCode.AXA,
    shcs: ['AXA'], // EK's own product code, wanted as an SHC on every AXA booking
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.PXS]: {
    name: 'Emirates Fresh',
    code: Emirates.ProductCode.PXS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PXD]: {
    name: 'Emirates Fresh Breathe',
    code: Emirates.ProductCode.PXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PNF]: {
    name: 'Emirates Fresh Perishable Non Food',
    code: Emirates.ProductCode.PNF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.GENERAL,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.PXF]: {
    name: 'Emirates Fresh Perishable Frozen',
    code: Emirates.ProductCode.PXF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXM]: {
    name: 'Emirates Medical Devices',
    code: Emirates.ProductCode.AXM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AXS]: {
    name: 'Emirates Pharma',
    code: Emirates.ProductCode.AXS,
    shcs: [IATA.HandlingCode.PIL],
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.AXD]: {
    name: 'Emirates Pharma Plus',
    code: Emirates.ProductCode.AXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXT]: {
    name: 'Emirates Vital',
    code: Emirates.ProductCode.AXT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VAL]: {
    name: 'Emirates Secure VAL',
    code: Emirates.ProductCode.VAL,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VUN]: {
    name: 'Emirates Secure VUN',
    code: Emirates.ProductCode.VUN,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AOG]: {
    name: 'Emirates AOG',
    code: Emirates.ProductCode.AOG,
    shcs: [IATA.HandlingCode.AOG],
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
  [Emirates.ProductCode.PAX]: {
    name: 'Emirates DG-PAX',
    code: Emirates.ProductCode.PAX,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.CAO]: {
    name: 'Emirates DG-CAO',
    code: Emirates.ProductCode.CAO,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
};

export const AC_PRODUCT_CODE_PELI: AirlineProductToPeliMap<AirCanada.ProductCode> = {
  [AirCanada.ProductCode.AFR]: {
    name: 'AC General - Standard',
    code: AirCanada.ProductCode.AFR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPR]: {
    name: 'AC General - Priority1',
    code: AirCanada.ProductCode.PPR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.ADG]: {
    name: 'AC DGR - Standard',
    code: AirCanada.ProductCode.ADG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PDG]: {
    name: 'AC DGR - Priority1',
    code: AirCanada.ProductCode.PDG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.PPS]: {
    name: 'AC Pharmacair Simple',
    code: AirCanada.ProductCode.PPS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPP]: {
    name: 'AC Pharmacair Plus',
    code: AirCanada.ProductCode.PPP,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.AFH]: {
    name: 'AC Fresh - Standard',
    code: AirCanada.ProductCode.AFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PFH]: {
    name: 'AC Fresh - Priority',
    code: AirCanada.ProductCode.PFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
};

export const QR_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Qatar.ProductCode> = {
  [Qatar.ProductCode.GCR]: {
    name: 'GCR',
    code: Qatar.ProductCode.GCR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.XPS]: {
    name: 'Q-Plus',
    code: Qatar.ProductCode.GCR,
    shcs: [IATA.HandlingCode.XPS],
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [Qatar.ProductCode.VIC]: {
    name: 'Q-Prime',
    code: Qatar.ProductCode.GCR,
    shcs: [IATA.HandlingCode.VIC],
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
  [Qatar.ProductCode.QRDG]: {
    name: 'QRDG',
    code: Qatar.ProductCode.QRDG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.PHARM]: {
    name: 'PHARM',
    code: Qatar.ProductCode.PHARM,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.SLIFT]: {
    name: 'SLIFT',
    code: Qatar.ProductCode.SLIFT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.QFR]: {
    name: 'QFR',
    code: Qatar.ProductCode.QFR,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.SEM]: {
    name: 'SEM',
    code: Qatar.ProductCode.SEM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
};

/**
 * TK returns one rate per product on every flight combination. The applicable rate is TK's
 * non-express ("TK Smart") rate; the other two are their express products.
 */
export const TK_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Turkish.ProductCode> = {
  [Turkish.ProductCode.SMART]: {
    name: 'TK Smart',
    code: Turkish.ProductCode.SMART,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Turkish.ProductCode.PREMIUM]: {
    name: 'TK Premium',
    code: Turkish.ProductCode.PREMIUM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [Turkish.ProductCode.URGENT]: {
    name: 'TK Urgent',
    code: Turkish.ProductCode.URGENT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
};
